import React, { useState } from "react";
import { useTaskGenerator } from "../hooks/useTaskGenerator";
import {
  TASK_TYPE_LABELS,
  TASK_TYPE_ICONS,
  TASK_TYPE_COLORS,
  type TaskType,
  type TaskConfig,
} from "../lib/taskEngine";
import type { Classroom, ClassroomMember } from "../lib/classroomEngine";

interface TaskGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacherId: string;
  teacherName?: string;
  type: TaskType;
  title: string;
  description?: string;
  config: TaskConfig;
  classrooms?: Classroom[];
  classroomMembers?: ClassroomMember[];
}

export const TaskGeneratorModal: React.FC<TaskGeneratorModalProps> = ({
  isOpen,
  onClose,
  teacherId,
  teacherName,
  type,
  title,
  description,
  config,
  classrooms = [],
  classroomMembers = [],
}) => {
  const { state, createTask, reset } = useTaskGenerator();
  const [taskTitle, setTaskTitle] = useState(title);
  const [classroomId, setClassroomId] = useState<string>("");
  const [assignAllStudents, setAssignAllStudents] = useState(true);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [taskDesc, setTaskDesc] = useState(description || "");
  const [useAudio, setUseAudio] = useState(true);
  const [maxAttempts, setMaxAttempts] = useState(10);
  const [showFeedback, setShowFeedback] = useState(true);
  const [expiresInHours, setExpiresInHours] = useState<number | undefined>(undefined);

  if (!isOpen) return null;

  const membersInClass = classroomId
    ? classroomMembers.filter((m) => m.classroomCode === classroomId)
    : [];

  const handleCreate = async () => {
    await createTask({
      teacherId,
      teacherName,
      type,
      title: taskTitle,
      description: taskDesc,
      config,
      useAudio,
      maxAttempts,
      showFeedback,
      expiresInHours,
      classroomId: classroomId || undefined,
      assignedStudentIds:
        classroomId && !assignAllStudents && selectedStudentIds.length > 0
          ? selectedStudentIds
          : undefined,
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCopyCode = () => {
    if (state.generatedCode) {
      navigator.clipboard.writeText(state.generatedCode);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold"
            style={{ backgroundColor: TASK_TYPE_COLORS[type] }}
          >
            {TASK_TYPE_ICONS[type]}
          </span>
          <div>
            <h3 className="text-sm font-bold text-slate-800">Crear tarea</h3>
            <p className="text-xs text-slate-500">{TASK_TYPE_LABELS[type]}</p>
          </div>
          <button
            onClick={handleClose}
            className="ml-auto w-7 h-7 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors text-lg leading-none"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {state.generatedCode ? (
            /* ─── Código generado ─── */
            <div className="space-y-4">
              <div className="text-center space-y-2">
                <div className="text-xs text-slate-500 font-medium uppercase tracking-wider">
                  Código de la tarea
                </div>
                <div className="text-3xl font-mono font-bold text-indigo-600 tracking-widest bg-indigo-50 rounded-lg py-3 border border-indigo-100">
                  {state.generatedCode}
                </div>
                <p className="text-xs text-slate-500">
                  {classroomId
                    ? "Los alumnos inscritos en esa clase la verán en su panel"
                    : "Comparte este código con tus estudiantes"}
                </p>
              </div>

              <button
                onClick={handleCopyCode}
                className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
              >
                📋 Copiar código
              </button>

              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-1">
                <div className="font-medium text-slate-800">Resumen de la tarea:</div>
                <div>Título: {taskTitle}</div>
                <div>Tipo: {TASK_TYPE_LABELS[type]}</div>
                <div>Validación por audio: {useAudio ? "Sí" : "No"}</div>
                <div>Intentos máximos: {maxAttempts}</div>
                {expiresInHours && <div>Expira en: {expiresInHours}h</div>}
                {classroomId && (
                  <div>
                    Solo clase: {classrooms.find((c) => c.id === classroomId)?.title || classroomId}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ─── Formulario de creación ─── */
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Título de la tarea</label>
                <input
                  type="text"
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  placeholder="Ej: Practica el acorde C Mayor"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Descripción (opcional)</label>
                <textarea
                  value={taskDesc}
                  onChange={(e) => setTaskDesc(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none"
                  placeholder="Instrucciones para el estudiante..."
                />
              </div>

              {classrooms.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">
                    Asignar a una clase (opcional)
                  </label>
                  <select
                    value={classroomId}
                    onChange={(e) => {
                      setClassroomId(e.target.value);
                      setAssignAllStudents(true);
                      setSelectedStudentIds([]);
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Cualquiera con el código de tarea</option>
                    {classrooms.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title} ({c.id})
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-slate-500">
                    Si eliges una clase, la tarea aparece en el panel de los alumnos inscritos.
                  </p>
                </div>
              )}

              {classroomId && membersInClass.length > 0 && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={assignAllStudents}
                      onChange={(e) => {
                        setAssignAllStudents(e.target.checked);
                        if (e.target.checked) setSelectedStudentIds([]);
                      }}
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                    />
                    <span className="text-sm text-slate-700">Todos los alumnos de la clase</span>
                  </label>
                  {!assignAllStudents && (
                    <div className="max-h-32 overflow-y-auto space-y-1 pl-1">
                      {membersInClass.map((m) => (
                        <label key={m.id} className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={selectedStudentIds.includes(m.studentId)}
                            onChange={(e) => {
                              setSelectedStudentIds((prev) =>
                                e.target.checked
                                  ? [...prev, m.studentId]
                                  : prev.filter((id) => id !== m.studentId)
                              );
                            }}
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                          />
                          <span className="text-slate-700">{m.studentName || "Estudiante"}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Intentos máximos</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={maxAttempts}
                    onChange={(e) => setMaxAttempts(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Expira en (horas)</label>
                  <select
                    value={expiresInHours || ""}
                    onChange={(e) => setExpiresInHours(e.target.value ? Number(e.target.value) : undefined)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Nunca</option>
                    <option value={24}>24 horas</option>
                    <option value={48}>48 horas</option>
                    <option value={72}>72 horas</option>
                    <option value={168}>1 semana</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useAudio}
                    onChange={(e) => setUseAudio(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-700">Validar con micrófono</span>
                </label>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showFeedback}
                    onChange={(e) => setShowFeedback(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-700">Mostrar feedback inmediato</span>
                </label>
              </div>

              {state.error && (
                <div className="text-xs text-rose-600 bg-rose-50 rounded-lg p-2 border border-rose-100">
                  ⚠ {state.error}
                </div>
              )}

              <button
                onClick={handleCreate}
                disabled={state.isCreating || !taskTitle.trim()}
                className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {state.isCreating ? "Generando código..." : "🎯 Generar código de tarea"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
